import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type {
  StripeCardExpiryElement,
  StripeCardCvcElement,
} from '@stripe/stripe-js';
import OptimusButton from '../../../common/library/Button/Button';
import OptimusInput from '../../../common/library/Input/Input';
import { TestIds } from '../../../assets/testIds';
import StripeLogo from '../../../assets/images/stripe_powered_logo.png';
import { StripeMembershipPaymentRequest } from '../../../common/models/StripePayment';
import { getTaxEstimate } from '../../../api/memberAPI';
import {
  INVALID_PROMO_CODE,
  normalizePromotionCode,
  resolvePromotionAmount,
} from '../../../common/membershipPromotion';

const STRIPE_FONT_FAMILY = 'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif';
const PROMO_CODE_VALIDATION_DELAY_MS = 500;

type MembershipUpgradeFormData = {
  cardholderName: string;
  zipCode: string;
  acceptedTerms: boolean;
};

const ERROR_CODE_TO_TRANSLATION: Record<string, string> = {
  SN_CARD_DECLINED: 'memberships.upgradeModal.errors.cardDeclined',
  SN_INSUFFICIENT_FUNDS: 'memberships.upgradeModal.errors.insufficientFunds',
  SN_EXPIRED_CARD: 'memberships.upgradeModal.errors.expiredCard',
  SN_INCORRECT_CVC: 'memberships.upgradeModal.errors.invalidCvc',
  SN_AUTHENTICATION_FAILED: 'memberships.upgradeModal.errors.authenticationFailed',
  SN_INVALID_CARD_NUMBER: 'memberships.upgradeModal.errors.invalidCardNumber',
  SN_INVALID_PAYMENT_METHOD: 'memberships.upgradeModal.errors.cardDeclined',
  SN_INVALID_PROMO_CODE: 'memberships.upgradeModal.promoCode.invalid',
};

type UpgradePaymentModalProps = {
  open: boolean;
  onClose: () => void;
  tierName: string;
  billingFrequency: 'monthly' | 'annual';
  amount: number;
  onSubmit: (paymentData: StripeMembershipPaymentRequest) => void;
  onClearError?: () => void;
  upgradeStatus?: 'idle' | 'loading' | 'success' | 'error';
  upgradeError?: string | null;
  upgradeErrorCode?: string | null;
  initialPromoCode?: string;
};

export function UpgradePaymentModal({
  open,
  onClose,
  tierName,
  billingFrequency,
  amount,
  onSubmit,
  onClearError,
  upgradeStatus = 'idle',
  upgradeError = null,
  upgradeErrorCode = null,
  initialPromoCode,
}: UpgradePaymentModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<MembershipUpgradeFormData>({
    mode: 'onChange',
    defaultValues: {
      cardholderName: '',
      zipCode: '',
      acceptedTerms: false,
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isProcessing = upgradeStatus === 'loading' || isSubmitting;
  const [cardError, setCardError] = useState<string | null>(null);

  const cardExpiryRef = useRef<StripeCardExpiryElement | null>(null);
  const cardCvcRef = useRef<StripeCardCvcElement | null>(null);
  const zipInputRef = useRef<HTMLInputElement | null>(null);
  const promoCodeValidationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promoCodeRequestIdRef = useRef(0);
  const [cardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
  const [cardCvcComplete, setCardCvcComplete] = useState(false);
  const [cardFieldFocused, setCardFieldFocused] = useState(false);
  const [taxAmount, setTaxAmount] = useState<number | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(amount);
  const [effectiveAmount, setEffectiveAmount] = useState<number>(amount);
  const [isCalculatingTax, setIsCalculatingTax] = useState(false);
  const [taxError, setTaxError] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [isApplyingPromoCode, setIsApplyingPromoCode] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);

  const cardComplete = cardNumberComplete && cardExpiryComplete && cardCvcComplete;

  const acceptedTerms = watch('acceptedTerms');
  const zipCode = watch('zipCode');
  const normalizedPromoCode = normalizePromotionCode(promoCode);
  const hasUnappliedPromoCode = normalizedPromoCode.length > 0 &&
    normalizedPromoCode !== appliedPromoCode;

  const isFormComplete = isValid && acceptedTerms && cardComplete && taxAmount !== null &&
    !hasUnappliedPromoCode;

  let cardBorderColor = theme.palette.grey[300];
  if (cardError) cardBorderColor = theme.palette.error.main;
  else if (cardFieldFocused) cardBorderColor = theme.palette.brand.primary;
  const cardBorderWidth = cardFieldFocused ? '2px' : '1px';

  const cancelPendingPromoCodeValidation = useCallback(() => {
    if (promoCodeValidationTimerRef.current) {
      clearTimeout(promoCodeValidationTimerRef.current);
      promoCodeValidationTimerRef.current = null;
    }
    promoCodeRequestIdRef.current += 1;
  }, []);

  // Helper to reset all form states
  const resetFormState = useCallback(() => {
    cancelPendingPromoCodeValidation();
    reset();
    setCardError(null);
    setCardNumberComplete(false);
    setCardExpiryComplete(false);
    setCardCvcComplete(false);
    setTaxAmount(null);
    setTotalAmount(amount);
    setEffectiveAmount(amount);
    setTaxError(null);
    setPromoCode('');
    setAppliedPromoCode(null);
    setIsApplyingPromoCode(false);
    setPromoCodeError(null);
  }, [amount, cancelPendingPromoCodeValidation, reset]);

  // Reset form and all states when modal closes
  useEffect(() => {
    if (!open) {
      resetFormState();
      setIsSubmitting(false);
      setIsCalculatingTax(false);
    }
  }, [open, resetFormState]);

  // Pre-fill promo code from campaign URL param when modal opens and immediately
  // apply the discount if the code is valid (zip-based tax runs later on zip entry).
  useEffect(() => {
    if (open && initialPromoCode) {
      setPromoCode(initialPromoCode);
      const discountedAmount = resolvePromotionAmount(initialPromoCode.trim(), billingFrequency);
      if (discountedAmount !== null) {
        setAppliedPromoCode(initialPromoCode.trim());
        setEffectiveAmount(discountedAmount);
        setTotalAmount(discountedAmount);
      } else {
        setPromoCodeError(t('memberships.upgradeModal.promoCode.invalid'));
      }
    }
  }, [open, initialPromoCode, billingFrequency, t]);

  useEffect(() => () => {
    cancelPendingPromoCodeValidation();
  }, [cancelPendingPromoCodeValidation]);

  // Reset submitting state when upgrade fails
  useEffect(() => {
    if (upgradeStatus === 'error') {
      setIsSubmitting(false);
      if (upgradeErrorCode === INVALID_PROMO_CODE) {
        setPromoCodeError(t('memberships.upgradeModal.promoCode.invalid'));
        setAppliedPromoCode(null);
        setEffectiveAmount(amount);
        setTaxAmount(null);
        setTotalAmount(amount);
      }
    }
  }, [amount, t, upgradeErrorCode, upgradeStatus]);

  // Get billing frequency label
  const getBillingFrequencyLabel = useCallback(() => (billingFrequency === 'monthly' ?
    t('memberships.upgradeModal.membershipDetails.monthly') :
    t('memberships.upgradeModal.membershipDetails.annual')), [billingFrequency, t]);

  // Use memo for Options
  const cardNumberOptions = useMemo(
    () => ({
      showIcon: true,
      // disableLink: true,
      placeholder:
        t('memberships.upgradeModal.cardInformation.cardNumberPlaceholder') ||
        'Card number',
      style: {
        base: {
          fontSize: '14px',
          color: theme.palette.grey[900],
          fontFamily: STRIPE_FONT_FAMILY,
          '::placeholder': {
            color: theme.palette.grey[500],
          },
        },
        invalid: {
          color: theme.palette.error.main,
        },
      },
    }),
    [t, theme.palette.grey, theme.palette.error.main],
  );

  const cardExpiryOptions = useMemo(
    () => ({
      style: {
        base: {
          fontSize: '14px',
          color: theme.palette.grey[900],
          fontFamily: STRIPE_FONT_FAMILY,
          '::placeholder': {
            color: theme.palette.grey[500],
          },
        },
        invalid: {
          color: theme.palette.error.main,
        },
      },
    }),
    [theme.palette.grey, theme.palette.error.main],
  );

  const cardCVVOptions = useMemo(
    () => ({
      placeholder: t('memberships.upgradeModal.cardInformation.cvvPlaceholder') || 'CVV',
      style: {
        base: {
          fontSize: '14px',
          color: theme.palette.grey[900],
          fontFamily: STRIPE_FONT_FAMILY,
          '::placeholder': {
            color: theme.palette.grey[500],
          },
        },
        invalid: {
          color: theme.palette.error.main,
        },
      },
    }),
    [t, theme.palette.grey, theme.palette.error.main],
  );

  const formatAmount = (value: number) => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Compute button text based on state
  const getPayButtonText = () => {
    if (isProcessing) {
      return t('memberships.upgradeModal.footer.payButtonProcessing');
    }
    if (taxAmount !== null) {
      return t('memberships.upgradeModal.footer.payButtonWithTax', {
        total: formatAmount(totalAmount),
      });
    }
    return t('memberships.upgradeModal.footer.payButtonWithoutTax', {
      amount: formatAmount(effectiveAmount),
    });
  };

  // Calculate tax when user finishes entering zip code (onBlur)
  const calculateTax = async (zipCodeValue: string, taxableAmount = amount) => {
    // Reset tax state if zip code is empty or invalid
    if (!zipCodeValue || zipCodeValue.trim() === '') {
      setTaxAmount(null);
      setTotalAmount(taxableAmount);
      setTaxError(null);
      setIsCalculatingTax(false);
      return;
    }

    // Only calculate for a complete US ZIP or ZIP+4 value.
    if (!/^\d{5}(-\d{4})?$/.test(zipCodeValue)) {
      setTaxAmount(null);
      setTotalAmount(taxableAmount);
      setTaxError(null);
      return;
    }

    promoCodeRequestIdRef.current += 1;
    const requestId = promoCodeRequestIdRef.current;
    setIsCalculatingTax(true);
    setTaxError(null);

    try {
      const response = await getTaxEstimate({
        amount: taxableAmount,
        billingFrequency,
        zip: zipCodeValue,
      });

      if (requestId !== promoCodeRequestIdRef.current) return;

      if (response && response.taxAmount !== undefined && response.totalAmount !== undefined) {
        const responseBaseAmount = Number(response.baseAmount);
        const resolvedAmount = Number.isFinite(responseBaseAmount) ?
          responseBaseAmount : taxableAmount;
        setEffectiveAmount(resolvedAmount);
        setTaxAmount(response.taxAmount);
        setTotalAmount(response.totalAmount);
      } else {
        throw new Error('Invalid response from tax estimation API');
      }
    } catch (error) {
      if (requestId !== promoCodeRequestIdRef.current) return;

      setTaxError(t('memberships.upgradeModal.errors.taxCalculationError'));
      setTotalAmount(taxableAmount);
      setTaxAmount(null);
    } finally {
      if (requestId === promoCodeRequestIdRef.current) {
        setIsCalculatingTax(false);
      }
    }
  };

  const validatePromoCode = async (promoCodeValue: string, zipCodeValue: string) => {
    const codeToValidate = normalizePromotionCode(promoCodeValue);
    if (!codeToValidate || !/^\d{5}(-\d{4})?$/.test(zipCodeValue)) return;
    if (codeToValidate === appliedPromoCode && taxAmount !== null) return;

    promoCodeRequestIdRef.current += 1;
    const requestId = promoCodeRequestIdRef.current;
    setPromoCodeError(null);
    setTaxError(null);
    onClearError?.();

    const discountedAmount = resolvePromotionAmount(codeToValidate, billingFrequency);
    if (discountedAmount === null) {
      setAppliedPromoCode(null);
      setEffectiveAmount(amount);
      setPromoCodeError(t('memberships.upgradeModal.promoCode.invalid'));
      setTaxAmount(null);
      setTotalAmount(amount);
      setIsApplyingPromoCode(false);
      return;
    }

    setIsApplyingPromoCode(true);

    try {
      const response = await getTaxEstimate({
        amount: discountedAmount,
        billingFrequency,
        zip: zipCodeValue,
      });

      if (requestId !== promoCodeRequestIdRef.current) return;

      if (response.taxAmount === undefined || response.totalAmount === undefined) {
        throw new Error('Invalid response from tax estimation API');
      }

      setAppliedPromoCode(codeToValidate);
      setEffectiveAmount(discountedAmount);
      setTaxAmount(response.taxAmount);
      setTotalAmount(response.totalAmount);
    } catch (error) {
      if (requestId !== promoCodeRequestIdRef.current) return;

      setAppliedPromoCode(null);
      setEffectiveAmount(discountedAmount);
      setTaxError(t('memberships.upgradeModal.errors.taxCalculationError'));
      setTotalAmount(discountedAmount);
      setTaxAmount(null);
    } finally {
      if (requestId === promoCodeRequestIdRef.current) {
        setIsApplyingPromoCode(false);
      }
    }
  };

  const handlePromoCodeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const nextPromoCode = event.target.value;
    const nextNormalizedPromoCode = normalizePromotionCode(nextPromoCode);

    cancelPendingPromoCodeValidation();
    setIsApplyingPromoCode(false);
    setIsCalculatingTax(false);
    setPromoCode(nextPromoCode);
    setPromoCodeError(null);
    setTaxError(null);
    onClearError?.();

    if (nextNormalizedPromoCode !== appliedPromoCode) {
      setAppliedPromoCode(null);
      setEffectiveAmount(amount);
      setTaxAmount(null);
      setTotalAmount(amount);
    }

    if (!nextNormalizedPromoCode) {
      if (/^\d{5}(-\d{4})?$/.test(zipCode)) {
        calculateTax(zipCode, amount);
      }
      return;
    }

    if (!/^\d{5}(-\d{4})?$/.test(zipCode)) return;

    promoCodeValidationTimerRef.current = setTimeout(() => {
      promoCodeValidationTimerRef.current = null;
      validatePromoCode(nextPromoCode, zipCode);
    }, PROMO_CODE_VALIDATION_DELAY_MS);
  };

  const handlePromoCodeBlur = () => {
    if (promoCodeValidationTimerRef.current) {
      clearTimeout(promoCodeValidationTimerRef.current);
      promoCodeValidationTimerRef.current = null;
    }
    validatePromoCode(promoCode, zipCode);
  };

  const onFormSubmit = async (data: MembershipUpgradeFormData) => {
    if (!stripe || !elements) {
      setCardError(t('error.stripeNotConfigured'));
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setCardError(t('error.stripeNotConfigured'));
      return;
    }

    setCardError(null);
    setIsSubmitting(true);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
        billing_details: {
          name: data.cardholderName,
          address: {
            postal_code: data.zipCode,
          },
        },
      });

      if (error) {
        const stripeCodeMap: Record<string, string> = {
          card_declined: 'memberships.upgradeModal.errors.cardDeclined',
          insufficient_funds: 'memberships.upgradeModal.errors.insufficientFunds',
          expired_card: 'memberships.upgradeModal.errors.expiredCard',
          incorrect_cvc: 'memberships.upgradeModal.errors.invalidCvc',
          invalid_number: 'memberships.upgradeModal.errors.invalidCardNumber',
        };
        const translationKey = error.decline_code ?
          stripeCodeMap[error.decline_code] :
          stripeCodeMap[error.code || ''];
        setCardError(translationKey ? t(translationKey) : (error.message || t('memberships.upgradeModal.errors.cardError')));
        setIsSubmitting(false);
        return;
      }

      if (paymentMethod) {
        onSubmit({
          cardholderName: data.cardholderName,
          zipCode: data.zipCode,
          acceptedTerms: data.acceptedTerms,
          paymentMethodId: paymentMethod.id,
          promoCode: appliedPromoCode || undefined,
          totalAmount,
        });
      }
    } catch (err) {
      setCardError(t('memberships.upgradeModal.errors.unexpectedError'));
      setIsSubmitting(false);
    }
  };

  const formattedNextBillingDate = useMemo(() => {
    const nextBillingDate = new Date();
    if (billingFrequency === 'monthly') {
      nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
    } else {
      nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
    }
    return nextBillingDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }, [billingFrequency]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={TestIds.upgradeModal.dialog}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: theme.spacing(3),
          }}
        >
          <Typography
            variant="headingSm"
            color={theme.palette.grey[900]}
            data-testid={TestIds.upgradeModal.title}
          >
            {t('memberships.upgradeModal.title', { tierName })}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="close"
            sx={{ color: theme.palette.grey[700] }}
            data-testid={TestIds.upgradeModal.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          {/* Form Section */}
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.spacing(1),
              p: theme.spacing(2.5),
              mb: theme.spacing(2),
            }}
          >
            {/* Cardholder Name */}
            <Box sx={{ mb: theme.spacing(2) }}>
              <Controller
                name="cardholderName"
                control={control}
                rules={{ required: t('memberships.upgradeModal.cardholderName.required') || 'Required' }}
                render={({ field }) => (
                  <OptimusInput
                    label={t('memberships.upgradeModal.cardholderName.label') || 'Cardholder Name'}
                    placeholderText={t('memberships.upgradeModal.cardholderName.placeholder') || 'John Doe'}
                    value={field.value}
                    onInputChange={(e) => field.onChange(e.target.value)}
                    error={!!errors.cardholderName}
                    helperText={errors.cardholderName?.message}
                    data-testid={TestIds.upgradeModal.cardholderNameInput}
                    width="100%"
                    sx={{ minWidth: '100%' }}
                    helperTextSx={{
                      fontSize: theme.spacing(1.5),
                      marginTop: theme.spacing(0.5),
                    }}
                  />
                )}
              />
            </Box>

            {/* Card Information */}
            <Box sx={{ mb: theme.spacing(2) }}>
              <Typography
                variant="labelMd"
                color={theme.palette.grey[700]}
                sx={{ mb: theme.spacing(1) }}
              >
                {t('memberships.upgradeModal.cardInformation.label')}
              </Typography>
              <Box
                sx={{
                  backgroundColor: theme.palette.common.white,
                  border: `${cardBorderWidth} solid ${cardBorderColor}`,
                  borderRadius: theme.spacing(0.75),
                  padding: theme.spacing(1.5),
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: theme.spacing(1.25),
                  minHeight: theme.spacing(5.25),
                }}
              >
                <Box sx={{
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: 'auto',
                  [theme.breakpoints.down('sm')]: {
                    flexBasis: '100%',
                  },
                }}>
                  <CardNumberElement
                    options={cardNumberOptions}
                    onFocus={() => setCardFieldFocused(true)}
                    onBlur={() => setCardFieldFocused(false)}
                    onChange={(e) => {
                      setCardNumberComplete(e.complete);
                      if (e.complete) cardExpiryRef.current?.focus();
                      if (e.error) {
                        setCardError(e.error.message);
                      } else {
                        setCardError(null);
                      }
                      if (upgradeError) onClearError?.();
                    }}
                  />
                </Box>

                <Box sx={{
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: theme.spacing(9.75),
                  [theme.breakpoints.down('sm')]: {
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    minWidth: 0,
                  },
                }}>
                  <CardExpiryElement
                    onReady={(el) => { cardExpiryRef.current = el; }}
                    options={cardExpiryOptions}
                    onFocus={() => setCardFieldFocused(true)}
                    onBlur={() => setCardFieldFocused(false)}
                    onChange={(e) => {
                      setCardExpiryComplete(e.complete);
                      if (e.complete) cardCvcRef.current?.focus();
                      if (e.error) {
                        setCardError(e.error.message);
                      } else {
                        setCardError(null);
                      }
                      if (upgradeError) onClearError?.();
                    }}
                  />
                </Box>

                <Box sx={{
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: theme.spacing(6.875),
                  [theme.breakpoints.down('sm')]: {
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    minWidth: 0,
                  },
                }}>
                  <CardCvcElement
                    onReady={(el) => { cardCvcRef.current = el; }}
                    options={cardCVVOptions}
                    onFocus={() => setCardFieldFocused(true)}
                    onBlur={() => setCardFieldFocused(false)}
                    onChange={(e) => {
                      setCardCvcComplete(e.complete);
                      if (e.complete) zipInputRef.current?.focus();
                      if (e.error) {
                        setCardError(e.error.message);
                      } else {
                        setCardError(null);
                      }
                      if (upgradeError) onClearError?.();
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: theme.spacing(10),
                    [theme.breakpoints.down('sm')]: {
                      flexGrow: 1,
                      flexShrink: 1,
                      flexBasis: 0,
                      minWidth: 0,
                    },
                    '& input': {
                      border: 'none',
                      outline: 'none',
                      fontSize: theme.spacing(1.75),
                      fontFamily: STRIPE_FONT_FAMILY,
                      color: errors.zipCode ? theme.palette.error.main : theme.palette.grey[900],
                      backgroundColor: 'transparent',
                      width: '100%',
                      padding: 0,
                      '&::placeholder': {
                        color: theme.palette.grey[500],
                      },
                    },
                  }}
                >
                  <input
                    placeholder={t('memberships.upgradeModal.zipCode.placeholder') || 'Zip Code'}
                    data-testid={TestIds.upgradeModal.zipCodeInput}
                    onFocus={() => setCardFieldFocused(true)}
                    {...register('zipCode', {
                      required: t('memberships.upgradeModal.zipCode.required') || 'Required',
                      pattern: {
                        value: /^\d{5}(-\d{4})?$/,
                        message: t('memberships.upgradeModal.zipCode.minLength') || 'Invalid zip code',
                      },
                      onChange: () => {
                        cancelPendingPromoCodeValidation();
                        setIsApplyingPromoCode(false);
                        setIsCalculatingTax(false);
                        setTaxAmount(null);
                        setTotalAmount(effectiveAmount);
                        setTaxError(null);
                        onClearError?.();
                      },
                      onBlur: (e) => {
                        if (normalizedPromoCode) {
                          validatePromoCode(promoCode, e.target.value);
                        } else {
                          calculateTax(e.target.value, amount);
                        }
                        setCardFieldFocused(false);
                      },
                    })}
                    ref={(el) => {
                      register('zipCode').ref(el);
                      zipInputRef.current = el;
                    }}
                  />
                </Box>
              </Box>
              {(cardError || taxError || errors.zipCode) && (
                <Typography
                  variant="labelSm"
                  color={theme.palette.error.main}
                  sx={{ mt: theme.spacing(0.5) }}
                >
                  {cardError || taxError || errors.zipCode?.message}
                </Typography>
              )}
              {upgradeError && upgradeErrorCode !== INVALID_PROMO_CODE && (
                <Typography
                  variant="labelSm"
                  color={theme.palette.error.main}
                  sx={{ mt: theme.spacing(0.5) }}
                  data-testid={TestIds.upgradeModal.errorMessage}
                >
                  {upgradeErrorCode && ERROR_CODE_TO_TRANSLATION[upgradeErrorCode] ?
                    t(ERROR_CODE_TO_TRANSLATION[upgradeErrorCode]) :
                    upgradeError}
                </Typography>
              )}
            </Box>

            {/* Promo Code */}
            <Box sx={{ mb: theme.spacing(2) }}>
              <OptimusInput
                label={t('memberships.upgradeModal.promoCode.label') || 'Promo Code'}
                placeholderText={t('memberships.upgradeModal.promoCode.placeholder') ||
                  'e.g. UPGRADE20'}
                value={promoCode}
                onInputChange={handlePromoCodeChange}
                onBlur={handlePromoCodeBlur}
                disabled={isProcessing}
                error={!!promoCodeError}
                data-testid={TestIds.upgradeModal.promoCodeInput}
                width="100%"
                sx={{ minWidth: 0 }}
                inputProps={{ maxLength: 100 }}
              />
              {promoCodeError && (
                <Typography
                  variant="labelSm"
                  color={theme.palette.error.main}
                  sx={{ mt: theme.spacing(0.5) }}
                  data-testid={TestIds.upgradeModal.promoCodeError}
                >
                  {promoCodeError}
                </Typography>
              )}
            </Box>

            {/* Membership Details */}
            <Box sx={{ mt: theme.spacing(3) }}>
              <Typography variant='bodyLgProminent'>
                {t('memberships.upgradeModal.membershipDetails.heading')}
              </Typography>
              <Divider
                sx={{ my: theme.spacing(1.5), borderColor: theme.palette.grey[300] }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5) }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: theme.spacing(1.5) }}>
                    <Typography variant="bodyMd" color={theme.palette.grey[600]}>
                      {t('memberships.upgradeModal.membershipDetails.plan')}
                    </Typography>
                    <Typography variant="body7" color={theme.palette.grey[900]}>
                      {t('memberships.upgradeModal.membershipDetails.planValue', { tierName })}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: theme.palette.grey[300] }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: theme.spacing(1.5) }}>
                    <Typography variant="bodyMd" color={theme.palette.grey[600]}>
                      {t('memberships.upgradeModal.membershipDetails.billingFrequency')}
                    </Typography>
                    <Typography variant="body7" color={theme.palette.grey[900]}>
                      {getBillingFrequencyLabel()}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: theme.palette.grey[300] }} />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: theme.spacing(1.5) }}>
                    <Typography variant="bodyMd" color={theme.palette.grey[600]}>
                      {t('memberships.upgradeModal.membershipDetails.amount')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                      {(isCalculatingTax || isApplyingPromoCode) ? (
                        <CircularProgress size={16} sx={{ color: theme.palette.grey[600] }} />
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body7"
                            color={theme.palette.grey[900]}
                            data-testid={TestIds.upgradeModal.amount}
                          >
                            {taxAmount !== null ?
                              t('memberships.upgradeModal.membershipDetails.amountWithTax', {
                                amount: formatAmount(effectiveAmount),
                                tax: formatAmount(taxAmount),
                              }) :
                              t('memberships.upgradeModal.membershipDetails.amountWithoutTax', {
                                amount: formatAmount(effectiveAmount),
                              })
                            }
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ borderColor: theme.palette.grey[300] }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="bodyMd" color={theme.palette.grey[600]}>
                    {t('memberships.upgradeModal.membershipDetails.nextBillingDate')}
                  </Typography>
                  <Typography variant="body7" color={theme.palette.grey[900]}>
                    {formattedNextBillingDate}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Terms Checkbox */}
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              borderRadius: theme.spacing(1),
              p: theme.spacing(2),
              mb: theme.spacing(2),
            }}
          >
            <Controller
              name="acceptedTerms"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isProcessing}
                      data-testid={TestIds.upgradeModal.termsCheckbox}
                      sx={{
                        color: theme.palette.brand.primary,
                        '&.Mui-checked': {
                          color: theme.palette.brand.primary,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="bodyMdProminent" color={theme.palette.grey[800]}>
                      {t('memberships.upgradeModal.terms.label')}{' '}
                      <a
                        href="https://servicenation.com/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: theme.palette.brand.primary, textDecoration: 'underline' }}
                      >
                        {t('memberships.upgradeModal.terms.link')}
                      </a>
                    </Typography>
                  }
                />
              )}
            />
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: theme.spacing(2),
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  [theme.breakpoints.down('sm')]: {
                    justifyContent: 'center',
                  },
                }}
              >
                <img src={StripeLogo} alt="Powered by Stripe" style={{ height: theme.spacing(3) }} />
                <Typography variant="labelSm" color={theme.palette.grey[600]}>
                  {t('memberships.upgradeModal.footer.securePayment')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                {(isCalculatingTax || isApplyingPromoCode || isProcessing) && (
                  <CircularProgress size={20} sx={{ color: theme.palette.brand.primary }} />
                )}
                <OptimusButton
                  buttonText={getPayButtonText()}
                  onClick={handleSubmit(onFormSubmit)}
                  disabled={!isFormComplete || isCalculatingTax || isApplyingPromoCode ||
                    isProcessing || !!upgradeError}
                  data-testid={TestIds.upgradeModal.submitButton}
                  customVariant="primary"
                  size="m"
                  fullWidth={false}
                  sx={{
                    minWidth: theme.spacing(25),
                    [theme.breakpoints.down('sm')]: {
                      width: '100%',
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
